import React from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ShoppingBag } from "lucide-react";

const Price = ({ value }) => <p className="text-xl font-bold text-gray-900">₹{Number(value).toLocaleString()}</p>;

const ShopButton = ({ product, accent }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(`/product/${product.id}`)}
      className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
      style={{ backgroundColor: accent }}
      data-testid={`store-shop-now-btn-${product.id}`}
    >
      <ShoppingBag className="w-4 h-4" />Shop Now
    </button>
  );
};

const ProductImage = ({ product, accent, className }) => (
  <div className={`relative bg-gray-100 overflow-hidden ${className}`}>
    {product.images?.[0] ? (
      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
    ) : (
      <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-12 h-12 text-gray-300" /></div>
    )}
    {product.commission_percent > 0 && (
      <Badge className="absolute top-3 left-3 text-white shadow-md border-0" style={{ backgroundColor: accent }}>{product.commission_percent}% Cashback</Badge>
    )}
  </div>
);

const GridCard = ({ product, accent }) => (
  <div className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow flex flex-col" data-testid={`store-product-card-${product.id}`}>
    <ProductImage product={product} accent={accent} className="aspect-square" />
    <div className="p-4 flex flex-col flex-1 gap-3">
      <h3 className="font-medium text-gray-900 line-clamp-2 flex-1">{product.name}</h3>
      <Price value={product.price} />
      <ShopButton product={product} accent={accent} />
    </div>
  </div>
);

const ListCard = ({ product, accent }) => (
  <div className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow flex" data-testid={`store-product-card-${product.id}`}>
    <ProductImage product={product} accent={accent} className="w-32 sm:w-48 flex-shrink-0 aspect-square" />
    <div className="p-4 flex flex-col flex-1 gap-2 min-w-0">
      <h3 className="font-semibold text-gray-900 line-clamp-1">{product.name}</h3>
      <p className="text-sm text-gray-500 line-clamp-2">{product.description}</p>
      <div className="mt-auto flex flex-col sm:flex-row sm:items-center gap-3">
        <Price value={product.price} />
        <div className="sm:w-40 sm:ml-auto"><ShopButton product={product} accent={accent} /></div>
      </div>
    </div>
  </div>
);

export const ProductCollection = ({ products, layout, accent }) => {
  if (layout === "list") {
    return <div className="space-y-4" data-testid="store-layout-list">{products.map((p) => <ListCard key={p.id} product={p} accent={accent} />)}</div>;
  }
  if (layout === "showcase" && products.length > 0) {
    const [hero, ...rest] = products;
    return (
      <div className="space-y-6" data-testid="store-layout-showcase">
        <div className="grid md:grid-cols-2 gap-6 bg-white rounded-3xl border border-gray-100 overflow-hidden" data-testid={`store-product-card-${hero.id}`}>
          <ProductImage product={hero} accent={accent} className="aspect-square md:aspect-auto md:min-h-[360px]" />
          <div className="p-6 md:p-10 flex flex-col justify-center gap-4">
            <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: accent }}>Spotlight</span>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">{hero.name}</h3>
            <p className="text-gray-600 line-clamp-4">{hero.description}</p>
            <Price value={hero.price} />
            <div className="max-w-xs"><ShopButton product={hero} accent={accent} /></div>
          </div>
        </div>
        {rest.length > 0 && <GridList products={rest} accent={accent} />}
      </div>
    );
  }
  return <GridList products={products} accent={accent} />;
};

const GridList = ({ products, accent }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="store-layout-grid">
    {products.map((p) => <GridCard key={p.id} product={p} accent={accent} />)}
  </div>
);
